import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, inr, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button, Chip } from "./ui";
import {
  ChevronDown, PlusCircle, Building2, Users, Crown, Shield,
  ChevronRight, LogOut, Settings, Calendar, Mail
} from "lucide-react";

export default function ClubSwitcher({ className }) {
  const { clubs, club, user, switchClub, refreshMe, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(null);

  if (clubs.length <= 1 && !user) {
    return null;
  }

  const currentClub = club || clubs.find(c => c.id === user?.active_club_id);

  const handleSwitch = async (clubId) => {
    setSwitching(clubId);
    try {
      await switchClub(clubId);
      setOpen(false);
      // refreshMe will be called by switchClub, which updates clubs
      // Navigate to home to refresh all data
      nav("/home", { replace: true });
    } catch (err) {
      console.error("Switch club error:", err);
    } finally {
      setSwitching(null);
    }
  };

  const handleCreateClub = () => {
    setOpen(false);
    nav("/clubs/create");
  };

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    nav("/login", { replace: true });
  };

  const getBadgeColor = (planId) => {
    switch (planId) {
      case "starter": return "success";
      case "league": return "default";
      case "premier": return "warning";
      default: return "default";
    }
  };

  const getBadgeLabel = (planId) => {
    switch (planId) {
      case "starter": return "Starter";
      case "league": return "League";
      case "premier": return "Premier";
      default: return "Free";
    }
  };

  return (
    <div className={`relative ${className || ""}`}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-2xl bg-black/5 hover:bg-black/10 transition-colors btn-press"
        data-testid="club-switcher-btn"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <div className="h-7 w-7 rounded-xl bg-brand-100 grid place-items-center shrink-0">
          {currentClub?.logo ? (
            <img src={currentClub.logo} alt={currentClub.name} className="h-6 w-6 rounded-lg object-cover" />
          ) : (
            <Building2 size={16} className="text-brand" />
          )}
        </div>
        <span className="font-semibold text-ink truncate max-w-[120px] text-sm">
          {currentClub?.name || "Select Club"}
        </span>
        <ChevronDown size={14} className={`text-ink-muted transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div className="fixed left-4 right-4 top-[72px] sm:absolute sm:left-auto sm:right-0 sm:w-80 z-50 bg-white rounded-3xl border border-black/10 shadow-xl overflow-hidden animate-in slide-in-from-top-2">
            {/* Header */}
            <div className="p-3 border-b border-black/5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-ink text-sm">Switch Club</h3>
                <span className="text-xs text-ink-muted">{clubs.length} club{clubs.length !== 1 ? "s" : ""}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={handleCreateClub}
                data-testid="club-switcher-create-btn"
              >
                <PlusCircle size={16} /> Create New Club
              </Button>
            </div>

            {/* Club List */}
            <div className="divide-y divide-black/5 max-h-[50vh] overflow-y-auto">
              {clubs.map((c) => (
                <button
                  key={c.id}
                  onClick={() => handleSwitch(c.id)}
                  disabled={switching === c.id || c.id === currentClub?.id}
                  className={`w-full p-3 text-left flex items-start gap-3 hover:bg-black/5 transition-colors ${
                    c.id === currentClub?.id ? "bg-brand-50" : ""
                  }`}
                  data-testid={`club-switcher-${c.id}`}
                >
                  <div className="h-10 w-10 rounded-xl bg-brand-100 grid place-items-center flex-shrink-0">
                    {c.logo ? (
                      <img src={c.logo} alt={c.name} className="h-9 w-9 rounded-lg object-cover" />
                    ) : (
                      <Building2 size={20} className="text-brand" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-ink truncate">{c.name}</span>
                      {c.id === currentClub?.id && (
                      <Chip tone="success" className="text-xs">Current</Chip>
                    )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-ink-muted">
                      <span className="capitalize">{c.my_role || "member"}</span>
                      <span>•</span>
                      <Users size={10} /> {c.member_count || 0} members
                    </div>
                    {c.active_match && (
                      <div className="mt-1.5 flex items-center gap-1 text-xs text-brand">
                        <Calendar size={10} />
                        <span className="font-medium">{fmtDate(c.active_match.date)}</span>
                        <span>•</span>
                        <span className="truncate max-w-[150px]">{c.active_match.ground}</span>
                        <span>vs</span>
                        <span className="truncate max-w-[100px]">{c.active_match.opponent}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Chip
                      tone={c.subscription_badge === "premier" ? "warning" : c.subscription_badge === "league" ? "default" : "success"}
                      className="text-xs"
                    >
                      {getBadgeLabel(c.subscription_badge)}
                    </Chip>
                    {c.id !== currentClub?.id && !switching && (
                      <ChevronRight size={16} className="text-ink-muted" />
                    )}
                    {switching === c.id && (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-black/5 space-y-1">
              <button
                onClick={() => { setOpen(false); nav("/invitations"); }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-ink-muted hover:text-brand hover:bg-brand-50 transition-colors"
                data-testid="club-switcher-invitations"
              >
                <Mail size={18} />
                Manage Invitations
              </button>
              {(currentClub?.my_role === "owner" || currentClub?.my_role === "admin") && (
                <button
                  onClick={() => { setOpen(false); nav(`/clubs/${currentClub.id}/settings`); }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-ink-muted hover:text-brand hover:bg-brand-50 transition-colors"
                  data-testid="club-switcher-settings"
                >
                  <Settings size={18} />
                  Club Settings
                </button>
              )}
              {user && (
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-ink-muted hover:text-red-500 hover:bg-red-50 transition-colors"
                  data-testid="club-switcher-logout"
                >
                  <LogOut size={18} />
                  Sign out
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}